import {
  type RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState
} from "react";

// FormSection's vertical padding (see views/tasks/styles.tsx: `padding: 28px 0`).
const FORM_SECTION_PADDING_Y = 28;

type FormAnchoredHeight = {
  /** Attach to an element inside the right form column (e.g. the first form item). */
  formProbeRef: RefObject<HTMLDivElement | null>;
  /**
   * Callback ref to attach to the left column's panel; apply `maxHeight` to it. A callback ref
   * (rather than a plain ref object) is used so the measurement starts the moment the panel
   * mounts — the info column can open collapsed (its expanded state comes from a shared cookie)
   * and mount the panel only later, after this hook's effect has already run.
   */
  panelRef: (node: HTMLDivElement | null) => void;
  /** Max height to cap the left panel at, or undefined before the first measurement. */
  maxHeight: number | undefined;
};

/**
 * Keeps the left column no taller than the right form column so the right column is the
 * static anchor for the modal height (the action buttons stay pinned to the bottom) and the
 * left column scrolls once its content grows past the form.
 *
 * It measures the form column's *natural* content height (stable even when the flex row
 * stretches the form to match a taller left column, unlike scrollHeight/clientHeight), then
 * subtracts the padding/margin the left column's wrappers add around the panel — so the
 * whole left column, not just the panel, matches the form height.
 *
 * @param deps re-run the measurement when these change (e.g. content that alters the form
 *   column's height, like a conditional error message).
 */
export function useFormAnchoredHeight(
  deps: unknown[] = []
): FormAnchoredHeight {
  const formProbeRef = useRef<HTMLDivElement>(null);
  const [panel, setPanel] = useState<HTMLDivElement | null>(null);
  const panelRef = useCallback((node: HTMLDivElement | null) => {
    setPanel(node);
  }, []);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    const probe = formProbeRef.current;
    // probe -> wrapper div (added by CommonUserTaskNew) -> FormSection
    const formSection = probe?.parentElement?.parentElement;
    if (!formSection || !panel) {
      return;
    }

    const measure = () => {
      const last = formSection.lastElementChild;
      if (!last) {
        return;
      }
      const formNatural =
        last.getBoundingClientRect().bottom -
        formSection.getBoundingClientRect().top +
        FORM_SECTION_PADDING_Y;

      // Sum the padding/margin of every wrapper between the panel and the left flex item
      // (the sibling of the form column under the shared container).
      const stop = formSection.parentElement;
      let overhead = 0;
      let node: HTMLElement | null = panel.parentElement;
      while (node && node !== stop) {
        const cs = getComputedStyle(node);
        overhead +=
          Number.parseFloat(cs.paddingTop) +
          Number.parseFloat(cs.paddingBottom) +
          Number.parseFloat(cs.marginTop) +
          Number.parseFloat(cs.marginBottom);
        node = node.parentElement;
      }

      const next = Math.max(0, formNatural - overhead);
      setMaxHeight((prev) =>
        prev !== undefined && Math.abs(prev - next) < 1 ? prev : next
      );
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(formSection);
    for (const child of Array.from(formSection.children)) {
      observer.observe(child);
    }
    return () => observer.disconnect();
    // biome-ignore lint/correctness/useExhaustiveDependencies: deps are provided by the caller
  }, deps);

  return { formProbeRef, panelRef, maxHeight };
}
